import React, { useContext, useEffect, useRef, useState } from "react";
import { Link as ReactLink, useNavigate } from "react-router-dom";
import AppContext from "../../AppContext";
import {
  IconButton,
  Box,
  Button as MuiButton,
  CircularProgress,
  Container,
  Divider,
  InputBase,
  Stack,
  Switch as MuiSwitch,
  SvgIcon,
  Typography,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import PropTypes from "prop-types";
import { LinkAttachment } from "../basic/LinkAttachment.jsx";
import { Button } from "../basic/Button";
import { CategoryChip } from "../basic/CategoryChip";
import { Switch } from "../basic/Switch";
import { TextField } from "../basic/TextField";
import { ConditionsList } from "../complex/ConditionsList";
import { ScreenNavHeader } from "../complex/ScreenNavHeader";
import DateDialog from "../complex/DateDialog";
import PaymentDialog from "../complex/PaymentDialog";
import { ResponsiveToaster } from "../basic/ResponsiveToaster.jsx";
import LinkInsert from "../complex/LinkInsert";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DateRangeIcon from "@mui/icons-material/DateRange";
import TranslateIcon from "@mui/icons-material/Translate";
import useMediaQuery from "@mui/material/useMediaQuery";
import SelectedInterestsAutocomplete from "../complex/SelectedInterestsAutocomplete.jsx";
import { ResponsiveLayoutSidebar } from "../complex/ResponsiveLayoutSidebar";
import transitions from "./transitions.json";

const theme = createTheme({
  palette: {},
  components: {
    // MuiTextField: {
    //   styleOverrides: {
    //     root: {
    //     },
    //   },
    // },
  },
});

function toNaturalNumber(num) {
  // Check if the number is positive
  if (num < 1) {
    return 1; // The smallest natural number
  }

  // Ensure the number is an integer
  return Math.floor(num);
}

function convertToCustomFormat(isoString) {
  // Create a Date object from the ISO string
  let date = new Date(isoString);

  // Get the components of the date
  let day = String(date.getDate()).padStart(2, "0");
  let month = date.getMonth(); // 0-indexed
  let year = date.getFullYear();

  // Get the components of the time
  let hours = date.getHours();
  let minutes = String(date.getMinutes()).padStart(2, "0");

  // Determine AM/PM period and convert hours to 12-hour format
  let period = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12; // Convert to 12-hour format

  // Month names array
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Format the final string
  let formattedDate = `${day} ${monthNames[month]}, ${year} / ${String(
    hours
  ).padStart(2, "0")}:${minutes} ${period}`;

  return formattedDate;
}

const capitalize = (string) => {
  return string?.[0]?.toUpperCase() + string?.slice(1)?.toLowerCase();
};

/**
 * Primary UI component for user interaction
 */
export const CreateActivityScreen = ({ ...props }) => {
  const navigate = useNavigate();
  const [globalState, setGlobalState, q, p] = useContext(AppContext);
  const [state, setState] = React.useState({
    paymentDialogOpen: false,
    dateDialogOpen: false,
    loading: false,
    validated: false,
    init: true,
    submission: {
      subcategoriesNamed: {},
      location: {
        longitude: 55.2677267,
        latitude: 25.1941284,
      },
      ...p.submission,
    },
  });
  const [toasterMessage, setToasterMessage] = useState();

  const timeoutId = useRef(null);
  const datetimeValues = useRef(null);
  const mutex = useRef(0);

  useEffect(() => {
    if (!p.submission) navigate("/");

    // Reset submission state if not returning from
    // any activity creation screen
    if (p.journey !== "create-activity") {
      p.submission = {
        status: "PUBLISHED",
        interestLocationType: "PHYSICAL",
        countryKey: "",
        city: "Dubai",
        activityName: "",
        activityDescription: "",
        numberOfParticipants: "",
        conditions: [],
        location: {
          longitude: 55.2677267,
          latitude: 25.1941284,
        },
      };
      p.formErrors = {};
    }

    setState({
      ...state,
      submission: p.submission,
      init: true,
      errors: { ...p.formErrors },
    });

    p.prevPage = p.currentPage || "create-activity";
    p.currentPage = "create-activity";
    p.activityMode = "create";
    p.journey = "create-activity";

    // console.log("submissionObj", submissionObj);
  }, []);

  useEffect(() => {
    // Validate fields
    // ---------------------------------------------------------------

    const activityCondition =
      p.submission?.activityDescription?.lenth > 0 &&
      p.submission?.activityDescription?.length <= 900;

    if (
      p.submission?.activityName &&
      p.submission?.activityDescription?.length <= 900 &&
      p.submission?.activityStart &&
      p.submission?.activityEnd &&
      p.submission?.interestLocationType &&
      p.submission?.interestID &&
      (p.submission?.location?.latitude || p.submission?.link?.url) &&
      p.submission?.numberOfParticipants >= 2 &&
      p.submission?.numberOfParticipants <= 50 &&
      (p.submission.fromAge < p.submission.toAge ||
        (!p.submission.fromAge && !p.submission.toAge))
    ) {
      if (!state.validated) {
        setState({
          ...state,
          submission: p.submission,
          validated: true,
        });
      }
    } else if (state.validated) {
      setState({
        ...state,
        submission: p.submission,
        validated: false,
      });
    }
  }, [state]);

  const toggleSwitch = (event) => {
    if (p.submission?.payment?.currency) {
      const confirmResponse = window.confirm(
        "Are you sure you want to delete the payment method?"
      );

      if (confirmResponse) {
        p.submission = {
          ...p.submission,
          payment: {
            ...p.submission?.payment,
            currency: null,
            price: null,
            paymentMethod: null,
            link: null,
          },
        };
        setState({
          ...state,
          submission: p.submission,
        });
      }
    } else {
      setState({
        ...state,
        paymentDialogOpen: true, //!state.paymentDialogOpen,
      });
    }
  };

  const addNewCondition = () => {
    const newItems = state.submission?.conditions
      ? [...state.submission.conditions]
      : [];

    if (
      !state.submission.conditions ||
      state.submission.conditions?.length <= 15
    ) {
      newItems.push({
        id: (newItems.length + 1).toString(),
        content: "",
      });

      p.submission = {
        ...p.submission,
        conditions: newItems,
      };

      setState({
        ...state,
        submission: p.submission,
      });
    }
  };

  const removeCondition = (event) => {
    const index = event.currentTarget.getAttribute("index");

    const newItems = state.submission?.conditions
      ? [...state.submission?.conditions]
      : [];

    newItems.splice(index, 1);

    p.submission = {
      ...p.submission,
      conditions: newItems,
    };

    setState({
      ...state,
      submission: p.submission,
    });
  };

  const onChipClick = (event) => {
    const categoryName = event.currentTarget.getAttribute("categoryName");
    navigate(`/create-activity/category/${categoryName}`);
  };

  const onSwitchLocation = (switchIndex) => {
    // Set to gte if switch is upcoming and to lte if recent
    p.submission = {
      ...p.submission,
      locationType: switchIndex === 0 ? "PHYSICAL" : "ONLINE",
      interestLocationType: switchIndex === 0 ? "PHYSICAL" : "ONLINE",
      subcategoriesNamed: null,
      subcategories: null,
    };

    setState({
      ...state,
      submission: p.submission,
    });
  };

  const onChangeActivityName = (event) => {
    const value = event.currentTarget.value;
    if (value.length >= 200) {
      p.submission.activityName = value;
      p.formErrors = {
        ...state.errors,
        activityName: "Enter a title",
      };
      setState({
        ...state,
        errors: p.formErrors,
        submission: p.submission,
      });
    } else {
      p.submission.activityName = value;
      p.formErrors = {
        ...state.errors,
        activityName: false,
      };
      setState({
        ...state,
        errors: p.formErrors,
        submission: p.submission,
      });
    }
  };

  const onChangeActivityDescription = (event) => {
    const value = event.currentTarget.value;
    if (value.length >= 900) {
      p.submission.activityDescription = value.substring(0, 900);
      p.formErrors = {
        ...state.errors,
        activityDescription: "Enter a title",
      };
      setState({
        ...state,
        submission: p.submission,
        errors: p.formErrors,
      });
    } else {
      p.submission.activityDescription = value;
      p.formErrors = {
        ...state.errors,
        activityDescription: false,
      };
      setState({
        ...state,
        submission: p.submission,
        errors: p.formErrors,
      });
    }
  };

  const onChangeNoAttendeesField = (event) => {
    const value = toNaturalNumber(parseInt(event.currentTarget.value));
    if (typeof parseInt(value) !== "number") return;

    const regexPattern = /^[0-9]{1,3}$/;
    const isMatch = regexPattern.test(value);
    // Check the result
    if (2 <= parseInt(value) && parseInt(value) <= 50) {
      p.submission.numberOfParticipants = parseInt(value);
      p.formErrors = {
        ...state.errors,
        numberOfParticipants: false,
      };
      setState({
        ...state,
        submission: p.submission,
        errors: p.formErrors,
      });
    } else {
      if (event._reactName === "onBlur") {
        p.submission.numberOfParticipants = (() => {
          if (parseInt(value) < 2 || typeof parseInt(value) !== "number") {
            return 2;
          } else if (parseInt(value) > 50) {
            return 50;
          }
        })();
        p.formErrors = {
          ...state.errors,
          numberOfParticipants: false,
        };
        setState({
          ...state,
          submission: p.submission,
          errors: p.formErrors,
        });
      } else if (event._reactName === "onChange") {
        p.submission.numberOfParticipants = parseInt(value);
        p.formErrors = {
          ...state.errors,
          numberOfParticipants:
            "Enter a valid number for number of participants",
        };
        setState({
          ...state,
          submission: p.submission,
          errors: p.formErrors,
        });
      }
    }
  };

  const onChangeAgeField = (event) => {
    const value = event.currentTarget.value;
    if (typeof parseInt(value) !== "number") return;

    const fieldIndex = event.currentTarget.getAttribute("id");
    const minimum = 16;
    const maximum = 99;

    // Define your regex pattern
    const regexPattern = /^[0-9]{1,2}$/;

    // Use the test method
    const isMatch = regexPattern.test(value);

    var errorCount = 0;

    if (minimum) {
      if (parseInt(value) < minimum) {
        errorCount += 1;
      }
    }

    if (maximum) {
      if (parseInt(value) > maximum) {
        errorCount += 1;
      }
    }

    if (fieldIndex === "fromAge" && parseInt(value) > p.submission?.["toAge"]) {
      errorCount += 1;
    }
    if (fieldIndex === "toAge" && parseInt(value) < p.submission?.["fromAge"]) {
      errorCount += 1;
    }

    if (!isMatch) errorCount += 1;

    // Check the result
    if (errorCount === 0 || value.length === 0) {
      p.submission[fieldIndex] = value;
      p.formErrors = {
        ...state.errors,
        [fieldIndex]: false,
      };
      setState({
        ...state,
        submission: p.submission,
        errors: p.formErrors,
      });
    } else {
      if (event._reactName === "onBlur") {
        p.submission[fieldIndex] = fieldIndex === "fromAge" ? 16 : 99;
        p.formErrors = {
          ...state.errors,
          [fieldIndex]: false,
        };
        setState({
          ...state,
          submission: p.submission,
          errors: p.formErrors,
        });
      } else {
        p.submission[fieldIndex] = value;
        p.formErrors = {
          ...state.errors,
          [fieldIndex]:
            "Enter a valid number for" +
            (fieldIndex === "toAge" ? "To" : "From") +
            "Age",
        };
        setState({
          ...state,
          submission: p.submission,
          errors: p.formErrors,
        });
      }
    }
  };

  const onAddLink = (url) => {
    p.submission = {
      ...p.submission,
      link: {
        url: url,
      },
    };
    setState({
      ...state,
      submission: p.submission,
    });
  };

  const onClearLink = () => {
    p.submission = {
      ...p.submission,
      link: null,
    };
    setState({
      ...state,
      submission: p.submission,
    });
  };

  const onChangeAddressLine = (event) => {
    const value = event.currentTarget.value;
    if (value.length >= 200) {
      p.submission.addressLine = value.substring(0, 200);
      p.formErrors = {
        ...state.errors,
        addressLine: "Address description cannot exceed 200 characters.",
      };
      setState({
        ...state,
        submission: p.submission,
        errors: p.formErrors,
      });
    } else {
      p.submission.addressLine = value;
      p.formErrors = {
        ...state.errors,
        addressLine: false,
      };
      setState({
        ...state,
        submission: p.submission,
        errors: p.formErrors,
      });
    }
  };

  const onClickCreate = () => {
    if (new Date(p?.submission?.activityStart) <= new Date(Date.now())) {
      setState({
        ...state,
        severity: "error",
        validated: false,
      });
      setToasterMessage("Please set a start date and time in the future.");
    } else {
      setState({
        ...state,
        validated: true,
      });
      setToasterMessage("");
      navigate("/create-activity/privacy");
    }
  };

  const onClickBack = () => {
    props.setAppDialog({
      ...props.appDialog,
      confirmText: "Exit",
      dialogChildren: (
        <Stack px={3}>
          <Typography variant="body1">
            Are you sure you want to leave this page?
            <br />
            Your changes will not be saved.
          </Typography>
        </Stack>
      ),
      open: true,
      onConfirm: async () => {
        navigate("/");
      },
    });
  };

  const onChangeInterests = (obj) => {
    // if(!obj) return
    const interestsID = obj?.[0]?.id || undefined; //? interests.map((interest) => `"${interest.id}"`) : [];
    const newSubcategories = obj?.[0]?.parentID
      ? { [obj?.[0]?.parentID]: [obj?.[0]?.id] }
      : {};
    const newSubcategoriesNamed = obj?.[0]?.parentID
      ? { [obj?.[0]?.parentTitle]: [obj?.[0]?.title] }
      : {};
    const newCategories = obj?.[0]?.parentTitle ? [obj?.[0]?.parentTitle] : [];

    // console.log("interests", interests, interestsID);

    mutex.current = 1;
    p.mutex = mutex.current;
    p.submission = {
      ...p.submission,
      selectedInterests: obj,
      selectedInterestsID: interestsID,
      subcategories: newSubcategories,
      subcategoriesNamed: newSubcategoriesNamed,
      categories: newCategories,
      interestID: Object?.values(newSubcategories)?.[0],
    };

    p.submission = {
      ...p.submission,
      subcategories: newSubcategories,
      subcategoriesNamed: newSubcategoriesNamed,
      categories: newCategories,
      interestID: Object?.values(newSubcategories)?.[0],
    };

    p.submission.interestsFlat = (() => {
      var flatArr = [];
      Object.values(p.submission?.subcategories)?.forEach((arr) =>
        flatArr.push(...arr)
      );
      return flatArr;
    })();

    if (!obj || obj?.length < 3) {
      p.errors = {
        ...p.errors,
        selectedInterests: false,
        selectedInterestsID: false,
      };
    } else {
      p.errors = {
        ...p.errors,
        selectedInterests: "Please select at least 3 interests.",
        selectedInterestsID: "Please select at least 3 interests.",
      };
    }
    setState({
      ...state,
      submission: p.submission,
      errors: p.errors,
    });
  };


  const makeGeneric = (path) => {
    var split = path.split("/");
    split.splice(3, 1);
    split.push(":subcategory");
    return split.join("/");
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="sm">
        <Box>
          <Box mb={0}>
            <ScreenNavHeader onClick={onClickBack} title="Create Activity">
              <Button
                disabled={state.loading || !state.validated}
                size={"large"}
                onClick={onClickCreate}
              >
                Create
              </Button>
            </ScreenNavHeader>
          </Box>

          {!state.init ? (
            <Stack my={4} alignItems={"center"}>
              <CircularProgress sx={{ color: "#2D9CDB" }} />
            </Stack>
          ) : (
            <>
              <Box mb={2}>
                <TextField
                  fullWidth
                  required
                  error={state.errors?.activityName ? true : false}
                  label="Activity Name"
                  onChange={onChangeActivityName}
                  value={state.submission?.activityName}
                />
              </Box>
              <Box mb={2}>
                <Stack spacing={1}>
                  <TextField
                    minRows={3}
                    fullWidth
                    error={state.errors?.activityDescription ? true : false}
                    multiline
                    label="Description"
                    onChange={onChangeActivityDescription}
                    value={state.submission?.activityDescription}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Character count:{" "}
                    {state.submission?.activityDescription?.length}/900
                  </Typography>
                </Stack>
              </Box>
              <Stack
                mb={6}
                alignItems={{ xs: "start", lg: "center" }}
                direction={{ xs: "column", md: "row" }}
                justifyContent={"space-between"}
                spacing={{ xs: 1, lg: 0 }}
              >
                <Typography sx={{ minWidth: 200 }}>
                  Activity Location *
                </Typography>

                {state.submission?.interestLocationType && (
                  <Switch
                    defaultIs0={
                      state.submission?.interestLocationType === "PHYSICAL"
                        ? true
                        : false
                    }
                    callback={onSwitchLocation}
                    optionA={"Physical"}
                    optionB={"Online"}
                    // width={useMediaQuery(theme.breakpoints.up("sm")) ? 552 : 320}
                    // width={useMediaQuery(theme.breakpoints.up("sm")) ? 320 : 220}
                    width={320}
                  />
                )}
              </Stack>
              <Stack mb={2} direction={"row"} justifyContent={"space-between"}>
                <Typography>Activity Category *</Typography>

                {Object?.keys(state.submission?.subcategoriesNamed || {})?.length >
                  0 && (
                  <IconButton onClick={() => onChangeInterests([])}>
                    <SvgIcon>
                      <EditIcon />
                    </SvgIcon>
                  </IconButton>
                )}
              </Stack>

              <Box mb={6}>
                <Stack direction={"row"} flexWrap={"wrap"}>
                  {state.submission?.subcategoriesNamed &&
                    Object.keys(state.submission?.subcategoriesNamed)?.map(
                      (parentCategory, i) => {
                        return Object.values(
                          state.submission?.subcategoriesNamed[parentCategory]
                        ).map((subCategory, j) => (
                          <CategoryChip
                            sx={{ marginRight: 1, marginBottom: 1 }}
                            onClick={onChipClick}
                            categoryName={`${parentCategory} - ${subCategory}`}
                            label={`${parentCategory} - ${subCategory}`}
                          />
                        ));
                      }
                    )}
                </Stack>
                <Stack>
                  {Object?.keys(state.submission?.subcategoriesNamed || {})?.length ===
                    0 && (
                    <>
                      {/* <Typography variant="body2" color="text.secondary">
                        <em>Select a category</em>
                      </Typography> */}
                      <SelectedInterestsAutocomplete
                        label={"Search category"}
                        placeholder="Search category"
                        callback={onChangeInterests}
                        // interestLocationType={state.submission?.interestLocationType}
                        data={state?.submission?.selectedInterests}
                      />
                    </>
                  )}
                </Stack>
              </Box>
              <Stack mb={2} direction={"row"} justifyContent={"space-between"}>
                <Typography>Date &amp; Time * </Typography>
                <IconButton
                  onClick={() =>
                    setState({
                      ...state,
                      dateDialogOpen: true,
                    })
                  }
                >
                  <SvgIcon>
                    <EditIcon />
                  </SvgIcon>
                </IconButton>
              </Stack>
              <DateDialog
                ref={(thisElem) => (datetimeValues.current = thisElem)}
                title="Time and Date"
                open={state.dateDialogOpen}
                startDate={state?.submission?.activityStart}
                endDate={state?.submission?.activityEnd}
                buttonDisabled={state.buttonDisabled}
                onChangeCallback={(isValid) =>
                  setState({
                    ...state,
                    buttonDisabled: !isValid,
                  })
                }
                onConfirm={() => {
                  p.submission = {
                    ...p.submission,
                    activityStart:
                      datetimeValues?.current?.value?.split(",")?.[0],
                    activityEnd:
                      datetimeValues?.current?.value?.split(",")?.[1],
                  };
                  setState({
                    ...state,
                    dateDialogOpen: false,
                    submission: p.submission,
                  });
                }}
                onClose={() =>
                  setState({
                    ...state,
                    dateDialogOpen: false,
                  })
                }
              />

              {!state?.submission?.activityStart &&
                !state?.submission?.activityEnd && (
                  <Stack
                    mb={6}
                    direction={"row"}
                    spacing={1}
                    alignItems={"center"}
                  >
                    <SvgIcon>
                      <DateRangeIcon />
                    </SvgIcon>
                    <Typography variant="body2">
                      Select start and end times.
                    </Typography>
                  </Stack>
                )}
              {state?.submission?.activityStart &&
                state?.submission?.activityEnd && (
                  <>
                    <Stack
                      mb={1}
                      direction={"row"}
                      spacing={1}
                      alignItems={"center"}
                    >
                      <SvgIcon>
                        <DateRangeIcon />
                      </SvgIcon>
                      <Typography variant="body2">
                        {convertToCustomFormat(
                          state?.submission?.activityStart
                        )}
                      </Typography>
                    </Stack>
                    <Stack
                      mb={6}
                      direction={"row"}
                      spacing={1}
                      alignItems={"center"}
                    >
                      <SvgIcon>
                        <DateRangeIcon />
                      </SvgIcon>
                      <Typography variant="body2">
                        {convertToCustomFormat(state?.submission?.activityEnd)}
                      </Typography>
                    </Stack>
                  </>
                )}

              <>
                <Stack
                  mb={2}
                  direction={"row"}
                  justifyContent={"space-between"}
                >
                  <Typography>Location*</Typography>
                  {state.submission?.interestLocationType === "PHYSICAL" && (
                    <IconButton
                      component={ReactLink}
                      to="/create-activity/select-location"
                    >
                      <SvgIcon>
                        <EditIcon />
                      </SvgIcon>
                    </IconButton>
                  )}
                </Stack>

                {state.submission?.interestLocationType === "PHYSICAL" && (
                  <>
                    <Box mb={2}>
                      {state?.submission?.location?.q ? (
                        <Typography variant="body1">
                          {state?.submission?.location?.q}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          <em>Select the location</em>
                        </Typography>
                      )}
                    </Box>
                    <Box mb={1}>
                      <iframe
                        width="100%"
                        height="200"
                        style={{ border: "0" }}
                        loading="lazy"
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                        src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyCK_7n9C93rb_5VmGoqAyTr-FlceCui28U
              &q=${state?.submission?.location?.latitude},${state?.submission?.location?.longitude}`}
                      ></iframe>
                    </Box>

                    <Box mb={6}>
                      <TextField
                        onChange={onChangeAddressLine}
                        label="Address Description"
                        fullWidth
                        error={state.errors?.addressLine ? true : false}
                        value={state.submission?.addressLine}
                      />
                    </Box>
                  </>
                )}

                {state.submission?.interestLocationType === "ONLINE" && (
                  <Box mb={6}>
                    {!state.submission?.link?.url && (
                      <LinkInsert onClickHandler={onAddLink} callback={null} />
                    )}

                    {/* Link Preview */}
                    {state.submission?.link?.url && (
                      <LinkAttachment
                        onClearLink={onClearLink}
                        target="_blank"
                        link={state.submission?.link?.url}
                      />
                    )}
                  </Box>
                )}
              </>
              <Stack
                mb={6}
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <Box>
                  <Typography gutterBottom>Number of Attendees*</Typography>
                  <Typography gutterBottom variant="body2">
                    Including activity organizer
                  </Typography>
                </Box>
                <TextField
                  required
                  error={state.errors?.numberOfParticipants ? true : false}
                  placeholder="2+"
                  size="medium"
                  type="number"
                  sx={{ width: 68, textAlign: "center" }}
                  onChange={onChangeNoAttendeesField}
                  onBlur={onChangeNoAttendeesField}
                  value={parseInt(state.submission?.numberOfParticipants)}
                  InputProps={{
                    inputProps: { min: 2, max: 50, type: "number" },
                  }}
                />
              </Stack>
              <Stack direction="row" justifyContent={"space-between"} mb={2}>
                <Box>
                  <Typography gutterBottom>Paid Activity (optional)</Typography>
                  <Typography gutterBottom variant="body2">
                    Please note that we do not process payments
                  </Typography>
                </Box>

                <MuiSwitch
                  checked={state.submission?.payment?.currency ? true : false}
                  onChange={toggleSwitch}
                />
              </Stack>
              {(state.submission?.payment?.currency ||
                state.submission?.payment?.price ||
                state.submission?.payment?.paymentMethod ||
                state.submission?.payment?.link) && (
                <Stack direction="row" alignItems={"start"} spacing={1} mb={6}>
                  <Box flexGrow={1}>
                    <Stack direction={"row"} spacing={1} mb={2}>
                      <Typography>
                        {state.submission?.payment?.currency}
                      </Typography>
                      <Typography sx={{ width: 42 }}>
                        {parseFloat(
                          state.submission?.payment?.price
                        )?.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                    </Stack>

                    <Box>
                      <Typography gutterBottom>
                        Payment method:{" "}
                        {capitalize(state.submission?.payment?.paymentMethod)}
                      </Typography>
                      <Typography>
                        <ReactLink
                          target="_blank"
                          to={state.submission?.payment?.link?.url}
                        >
                          {state.submission?.payment?.link?.url}
                        </ReactLink>
                      </Typography>
                    </Box>
                  </Box>

                  <IconButton
                    onClick={() =>
                      setState({ ...state, paymentDialogOpen: true })
                    }
                  >
                    <SvgIcon
                      sx={{ marginLeft: "0 !important", fontWeight: 700 }}
                    >
                      <EditIcon />
                    </SvgIcon>
                  </IconButton>
                </Stack>
              )}
              <PaymentDialog
                title="Price and Payment"
                open={state.paymentDialogOpen}
                p={p}
                onConfirmCallback={(updatedPaymentObj) => {
                  p.submission = {
                    ...p.submission,
                    payment: updatedPaymentObj,
                  };
                  setState({
                    ...state,
                    paymentDialogOpen: false,
                    submission: p.submission,
                  });
                }}
                onClose={() => setState({ ...state, paymentDialogOpen: false })}
              />
              <Box mb={6}>
                <Divider />
              </Box>
              <Stack mb={1} direction={"row"} justifyContent={"space-between"}>
                <Typography gutterBottom>Photos (optional)</Typography>
                <IconButton component={ReactLink} to="/create-activity/photos">
                  <SvgIcon>
                    <EditIcon />
                  </SvgIcon>
                </IconButton>
              </Stack>
              <Box sx={{ overflowX: "scroll" }} mb={6}>
                <Stack
                  direction={"row"}
                  spacing={2}
                  sx={{ width: "fit-content" }}
                >
                  {state?.submission?.imagesData?.map((imageObj, i) => (
                    <Box
                      key={`image-${i}`}
                      sx={{
                        borderRadius: 4,
                        height: { xs: 165 },
                        width: { xs: 165 },
                        background: `url(${imageObj.url}) center`,
                        backgroundSize: "cover",
                      }}
                      src={imageObj.url}
                      alt={imageObj.title}
                    />
                  ))}
                </Stack>
              </Box>
              <Box mb={6}>
                <Typography gutterBottom>Conditions (optional)</Typography>
                <Box py={2}>
                  <ConditionsList
                    items={state.submission?.conditions}
                    setConditions={(items) =>
                      setState({ ...state, conditions: items })
                    }
                    removeHandler={removeCondition}
                  />
                </Box>

                <MuiButton
                  variant="text"
                  startIcon={
                    <SvgIcon>
                      <AddIcon />
                    </SvgIcon>
                  }
                  size="large"
                  onClick={addNewCondition}
                  sx={{
                    color: "#000",
                    textTransform: "none",
                  }}
                >
                  Add a condition
                </MuiButton>
              </Box>
              <Box mb={6}>
                <Box pb={2}>
                  <Typography gutterBottom>
                    Age restrictions (optional)
                  </Typography>
                </Box>

                <Stack
                  spacing={2}
                  direction={"row"}
                  justifyContent={"space-between"}
                >
                  <TextField
                    fullWidth
                    error={state.errors?.fromAge ? true : false}
                    label={"From age"}
                    type="number"
                    id="fromAge"
                    onChange={onChangeAgeField}
                    onBlur={onChangeAgeField}
                    InputProps={{ inputProps: { min: 16, max: 99 } }}
                    value={state.submission?.fromAge}
                  />
                  <TextField
                    fullWidth
                    error={state.errors?.toAge ? true : false}
                    id="toAge"
                    type="number"
                    label={"To age"}
                    onChange={onChangeAgeField}
                    onBlur={onChangeAgeField}
                    InputProps={{ inputProps: { min: 16, max: 99 } }}
                    value={state.submission?.toAge}
                  />
                </Stack>
              </Box>
              <Box mb={6}>
                <Box pb={2}>
                  <Stack
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    direction="row"
                  >
                    <Typography gutterBottom>Languages (optional)</Typography>
                    <IconButton
                      component={ReactLink}
                      to="/create-activity/select-language"
                    >
                      <SvgIcon>
                        <EditIcon />
                      </SvgIcon>
                    </IconButton>
                  </Stack>
                </Box>

                {state.submission?.selectedLanguagesNamed && (
                  <Stack direction={"row"} spacing={2}>
                    <SvgIcon>
                      <TranslateIcon />
                    </SvgIcon>
                    <Typography>
                      {state.submission?.selectedLanguagesNamed?.map((v, i) =>
                        `${v}`.concat(
                          i <
                            state.submission?.selectedLanguagesNamed?.length - 1
                            ? `, `
                            : ""
                        )
                      )}
                    </Typography>
                  </Stack>
                )}
              </Box>

              <Button
                fullWidth
                disabled={state.loading || !state.validated}
                size={"large"}
                onClick={onClickCreate}
              >
                Create
              </Button>
            </>
          )}
        </Box>

        <ResponsiveToaster
          sx={{ position: "sticky" }}
          severity={state.severity}
          open={toasterMessage ? true : false}
          toasterMessage={toasterMessage}
          setToasterMessage={() => setToasterMessage("")}
        />
      </Container>
    </ThemeProvider>
  );
};

export const CreateActivityScreenLayout = (props) => {
  return (
    <ResponsiveLayoutSidebar waitForUserData={props.waitForUserData}>
      <CreateActivityScreen {...props} />
    </ResponsiveLayoutSidebar>
  );
};

CreateActivityScreen.propTypes = {
  paid: PropTypes.bool,
  currency: PropTypes.string,
  price: PropTypes.number,
};

CreateActivityScreen.defaultProps = {
  color: "primary",
};
